import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getDistance } from 'geolib';
import {
  GoogleMap,
  LoadScript,
  Marker,
  Autocomplete,
  InfoWindow,
} from '@react-google-maps/api';
import markerIcon from './img/marker.png';
import { ReactComponent as CrossHairIcon } from './img/crosshair.svg';
import styled from 'styled-components';
import SingleLocation from './components/SingleLocation';

const locationsUrl =
  'https://www.burgerfi.com/wp-json/burgerfi-locations/v1/locations';
const apiKey = 'AIzaSyAykSgMwqjiQJIrz3k5GOFsYXcqyyh6fW0';
const libraries = ['places'];

export interface Location {
  name?: string;
  lat?: string;
  lng?: string;
  streetAddress?: string;
  locality?: string;
  region?: string;
  zip?: string;
  phone?: string;
  hours?: any;
  url?: string;
  offers_curbside?: boolean;
  distance?: number;
  orderUrl?: string;
}

function App() {
  const [locations, setLocations] = useState<Location[] | null>();
  const [results, setResults] = useState<Location[] | null>();
  const [showInfoWindow, setShowInfoWindow] = useState<Boolean>(false);
  const [infoWindowData, setInfoWindowData] = useState<any>({});
  const [autocomplete, setAutocomplete] = useState<any>(null);
  const [zoom, setZoom] = useState<number>(5);
  const [useBounds, setBounds] = useState<any>({
    lat: 33.522861,
    lng: -86.807701,
  });
  const [gettingResults, setGettingResults] = useState<boolean>(true);
  const getAllLocations = async () => {
    try {
      const response = await axios.get(locationsUrl);
      setLocations(response.data);
      setResults(response.data);
      setGettingResults(false);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    getAllLocations();
  }, []);

  const onLoad = (complete: any) => {
    setAutocomplete(complete);
  };

  const onPlaceChanged = async () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setGettingResults(true);
      setBounds({ lat, lng });
      setZoom(12);
      await sortResults({ lat, lng }, results);
      setGettingResults(false);
      // console.log('results', results);
    } else {
      console.log('Autocomplete is not loaded yet!');
    }
  };

  const handleMarkerClick = (single: any) => {
    if (single.lat && single.lng) {
      const coords = {
        lat: parseFloat(single.lat),
        lng: parseFloat(single.lng),
      };
      setInfoWindowData({ coords, ...single });
      setShowInfoWindow(true);
    }
  };

  const handleLocationClick = (single: any) => {
    if (single.lat && single.lng) {
      const coords = {
        lat: parseFloat(single.lat),
        lng: parseFloat(single.lng),
      };
      setInfoWindowData({ coords, ...single });
      setShowInfoWindow(true);
      setBounds(coords);
      setZoom(10);
    }
  };

  const findMyLocation = async () => {
    if ('geolocation' in navigator) {
      await navigator.geolocation.getCurrentPosition(
        (position: any) => {
          console.log('Location Available', position);
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setGettingResults(true);
          setBounds({ lat, lng });
          setZoom(12);
          sortResults({ lat, lng }, results);
          setGettingResults(false);
        },
        (err) => console.log(err)
      );
    } else {
      console.log('Not Available');
    }
  };

  const sortResults = async (point1: any, locationArray: any) => {
    const pointObj = {
      latitude: parseFloat(point1.lat),
      longitude: parseFloat(point1.lng),
    };

    await locationArray.sort((a: Location, b: Location) => {
      if (a.lat && b.lat && a.lng && b.lng) {
        const aObj = {
          latitude: parseFloat(a.lat),
          longitude: parseFloat(a.lng),
        };
        const bObj = {
          latitude: parseFloat(b.lat),
          longitude: parseFloat(b.lng),
        };
        const distA = getDistance(pointObj, aObj);
        a.distance = distA / 1609.344;
        const distB = getDistance(pointObj, bObj);
        b.distance = distB / 1609.344;

        return distA - distB;
      }
      if (!a.lat || !a.lng) {
        console.log('Missing', a);
        return 1;
      }
      if (!b.lat || !b.lng) {
        console.log('Missing', b);
        return -1;
      }
    });

    await setResults(locationArray);
    return true;
  };

  return (
    <LoadScript
      id="script-loader"
      googleMapsApiKey={apiKey}
      libraries={libraries}
    >
      {locations && locations.length > 0 ? (
        <Wrapper id="main">
          <Half className="listings">
            <h1 className="visuallyhidden">Find a Location</h1>
            <SearchHeader>
              <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                <>
                  <Label htmlFor="search">Find a Location</Label>
                  <SearchWrapper>
                    <Search
                      type="text"
                      name="search"
                      id="search"
                      placeholder="What's your City or ZIP?"
                    />
                    <TargetButton onClick={() => findMyLocation()}>
                      <span className="visuallyhidden">Find My Location</span>
                      <CrossHairIcon />
                    </TargetButton>
                  </SearchWrapper>
                </>
              </Autocomplete>
            </SearchHeader>
            <SearchResults>
              {results &&
                results.length > 0 &&
                gettingResults === false &&
                results.map((single: Location) => (
                  <SingleLocation
                    location={single}
                    key={single.lat}
                    click={() => handleLocationClick(single)}
                  />
                ))}
            </SearchResults>
          </Half>
          <Half className="mapWrapper">
            <GoogleMap
              id="circle-example"
              mapContainerStyle={{
                height: '100vh',
                width: '100%',
              }}
              zoom={zoom}
              center={useBounds}
              options={{
                disableDefaultUI: true,
                styles: [
                  {
                    elementType: 'geometry',
                    stylers: [
                      {
                        color: '#f5f5f5',
                      },
                    ],
                  },
                  {
                    elementType: 'labels.icon',
                    stylers: [
                      {
                        visibility: 'off',
                      },
                    ],
                  },
                  {
                    elementType: 'labels.text.fill',
                    stylers: [
                      {
                        color: '#616161',
                      },
                    ],
                  },
                  {
                    elementType: 'labels.text.stroke',
                    stylers: [
                      {
                        color: '#f5f5f5',
                      },
                    ],
                  },
                  {
                    featureType: 'administrative.land_parcel',
                    elementType: 'labels.text.fill',
                    stylers: [
                      {
                        color: '#bdbdbd',
                      },
                    ],
                  },
                  {
                    featureType: 'poi',
                    elementType: 'geometry',
                    stylers: [
                      {
                        color: '#eeeeee',
                      },
                    ],
                  },
                  {
                    featureType: 'poi',
                    elementType: 'labels.text.fill',
                    stylers: [
                      {
                        color: '#757575',
                      },
                    ],
                  },
                  {
                    featureType: 'poi.park',
                    elementType: 'geometry',
                    stylers: [
                      {
                        color: '#e5e5e5',
                      },
                    ],
                  },
                  {
                    featureType: 'poi.park',
                    elementType: 'labels.text.fill',
                    stylers: [
                      {
                        color: '#9e9e9e',
                      },
                    ],
                  },
                  {
                    featureType: 'road',
                    elementType: 'geometry',
                    stylers: [
                      {
                        color: '#ffffff',
                      },
                    ],
                  },
                  {
                    featureType: 'road.arterial',
                    elementType: 'labels.text.fill',
                    stylers: [
                      {
                        color: '#757575',
                      },
                    ],
                  },
                  {
                    featureType: 'road.highway',
                    elementType: 'geometry',
                    stylers: [
                      {
                        color: '#dadada',
                      },
                    ],
                  },
                  {
                    featureType: 'road.highway',
                    elementType: 'labels.text.fill',
                    stylers: [
                      {
                        color: '#616161',
                      },
                    ],
                  },
                  {
                    featureType: 'road.local',
                    elementType: 'labels.text.fill',
                    stylers: [
                      {
                        color: '#9e9e9e',
                      },
                    ],
                  },
                  {
                    featureType: 'transit.line',
                    elementType: 'geometry',
                    stylers: [
                      {
                        color: '#e5e5e5',
                      },
                    ],
                  },
                  {
                    featureType: 'transit.station',
                    elementType: 'geometry',
                    stylers: [
                      {
                        color: '#eeeeee',
                      },
                    ],
                  },
                  {
                    featureType: 'water',
                    elementType: 'geometry',
                    stylers: [
                      {
                        color: '#c9c9c9',
                      },
                    ],
                  },
                  {
                    featureType: 'water',
                    elementType: 'labels.text.fill',
                    stylers: [
                      {
                        color: '#9e9e9e',
                      },
                    ],
                  },
                ],
              }}
            >
              {locations.map((single: Location, val: number) => {
                if (single.lat && single.lng) {
                  const coords = {
                    lat: parseFloat(single.lat),
                    lng: parseFloat(single.lng),
                  };
                  return (
                    <Marker
                      position={coords}
                      icon={markerIcon}
                      clickable
                      key={val}
                      onClick={() => handleMarkerClick(single)}
                    />
                  );
                }
              })}
              {showInfoWindow && (
                <InfoWindow
                  position={infoWindowData.coords}
                  options={{
                    pixelOffset: new (window as any).google.maps.Size(0, -32),
                  }}
                >
                  <InfoWindowContainer>
                    <h3>{infoWindowData.name}</h3>
                    <p>
                      {infoWindowData.streetAddress} <br />
                      {infoWindowData.locality} {infoWindowData.region},{' '}
                      {infoWindowData.zip}
                    </p>
                    <p className="sm">{infoWindowData.phone}</p>
                    {infoWindowData.hours &&
                      infoWindowData.hours.map((hour: any) => (
                        <p className="sm">
                          {hour.label}: {hour.location_hours}
                        </p>
                      ))}
                    <a href={infoWindowData.url}>Location Page</a>
                  </InfoWindowContainer>
                </InfoWindow>
              )}
            </GoogleMap>
          </Half>
        </Wrapper>
      ) : (
        'Loading...'
      )}
    </LoadScript>
  );
}

export default App;

const Wrapper = styled.div`
  display: flex;
  align-content: top;
  align-items: top;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Half = styled.div`
  height: 100vh;
  overflow: hidden;

  &.listings {
    width: 33.334%;
    box-sizing: border-box;
    min-width: 530px;
  }

  &.mapWrapper {
    width: 66.667%;
  }

  @media (max-width: 768px) {
    width: 100% !important;
    &.listings {
      width: 100% !important;
      min-width: 0;
      height: 60vh;
      order: 2;
    }
    &.mapWrapper {
      height: 40vh;
      width: 100% !important;
      order: 1;
    }
  }
`;

const Search = styled.input`
  width: 100%;
  display: block;
  padding: 0.5rem 0.75rem;
  line-height: 1.25;
  font-size: 1rem;
  color: #464a4c;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-sizing: border-box;
`;

const SearchHeader = styled.div`
  color: #f6f6f6;
  padding: 40px 40px 0 40px;
`;

const SearchResults = styled.div`
  overflow-y: scroll;
  height: 100%;
  padding: 40px;
`;

const Label = styled.label`
  font-size: 30px;
  font-weight: bold;
  color: #000;
  margin-bottom: 15px;
  font-weight: lighter !important;
  display: block;
`;

const TargetButton = styled.button`
  margin-left: -1px;
  background-color: transparent;
  color: #8bbf3f;
  border: none;
  box-shadow: none;
  cursor: pointer;
  padding: 3px 15px;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;

  svg {
    width: 20px;
    margin-top: 2px;
    * {
      fill: #8bbf3f;
    }
  }

  &:hover {
    svg {
      * {
        fill: #444444;
      }
    }
  }
`;

const SearchWrapper = styled.div`
  display: flex;
`;

const InfoWindowContainer = styled.div`
  box-sizing: border-box;
  overflow: hidden;

  h3 {
    color: #8bbf3f;
    font-size: 16px;
    margin: 0;
    font-weight: 500;
    line-height: 1.1;
  }

  p {
    margin-bottom: 0.5em;
    font-size: 13px;
    margin-top: 5px;
    line-height: 1.3;

    &.sm {
      color: #979797;
      font-size: 12px;
      margin: 0;
    }
  }

  a {
    background: #8bbf3f;
    color: #4e4e4e;
    width: 100%;
    display: block;
    padding: 3px;
    min-width: 130px;
    width: 100%;
    margin-top: 10px;
    text-align: center;
    font-size: 12px;
    text-decoration: none;

    &:hover {
      background: #c2e096;
    }
  }
`;
